import React from 'react';
import { MapPinIcon, PlusIcon, XIcon } from 'lucide-react';
import { BUTTON_QUIET, FIELD, SECTION_HINT, SECTION_TITLE } from './adminStyles';
import type { CallSheetLocation } from '../../utils/callSheetLocations';

/**
 * WHERE THE DAY HAPPENS — 28 Oct 2026.
 *
 * One box held one address and one map link, and a two-location day was being
 * typed into it as "1. … 2. …" with no way to open the second on a phone.
 * Now each place is its own row with its own link.
 *
 * **The name is the working part.** It is short on purpose — "Red Rock",
 * "Marjan HQ" — because it is what the schedule's Where column says and what
 * the crew read at the top of the sheet. The address is for the driver; the
 * name is for everyone else, all day.
 *
 * No drag handle here, unlike the schedule. A running order has twenty rows
 * whose order is the whole point; a day has two or three places and their
 * order carries no meaning, so a grip would be furniture.
 */
interface CallSheetLocationsProps {
  locations: CallSheetLocation[];
  onAdd: () => void;
  onUpdate: (index: number, patch: Partial<CallSheetLocation>) => void;
  onRemove: (index: number) => void;
}
export function CallSheetLocations({
  locations,
  onAdd,
  onUpdate,
  onRemove
}: CallSheetLocationsProps) {
  return <section className="mt-14 border-t border-ruleDark pt-8" data-id="element-677" data-mp="byjzpta6xcvt">
      <h2 className={SECTION_TITLE} data-id="element-678" data-mp="bzv5fyn74upk">Locations &amp; Access</h2>
      <p className={SECTION_HINT} data-id="element-679" data-mp="g0x70m4l5iup">
        {locations.length > 1 ? 'Give each place a short name — the schedule offers those names, so nobody retypes an address.' : 'Add a place for each location the day visits. Each one keeps its own map link.'}
      </p>

      {locations.length === 0 && <p className="mt-6 font-display text-lg text-paper/40" data-id="element-680" data-mp="unqdr301hgba">No location yet.</p>}

      <ul className="mt-6 space-y-6" data-id="element-681" data-mp="zp5x8qmkjerk">
        {locations.map((location, index) => <li key={index} className="border border-paper/20 p-5" data-id="element-682" data-mp="13oybnjyn8f4u" data-mpi={index}>
            <div className="flex items-start gap-4" data-id="element-683" data-mp="30qdl8nzxuy83">
              <span aria-hidden="true" className="shrink-0 translate-y-2 text-paper/25" title={`Location ${index + 1}`} data-id="element-684" data-mp="30r5jy7ou1c2l">
                <MapPinIcon className="h-5 w-5" strokeWidth={1.25} data-id="element-685" data-mp="39jpkjdoopqq2" />
              </span>

              <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-686" data-mp="34rpdl1a75xjz">
                <label className="block" data-id="element-687" data-mp="3jglk2twlv2ih">
                  <span className="label block text-paper/40" data-id="element-688" data-mp="3jfk6ois4a3xl">Name</span>
                  <input value={location.name} onChange={event => onUpdate(index, {
                name: event.target.value
              })} placeholder="e.g. Red Rock" className={FIELD} data-id="element-689" data-mp="3eiadcs1lx84h" />
                </label>

                <label className="block sm:col-span-2" data-id="element-690" data-mp="1y38zn4ud2hvt">
                  <span className="label block text-paper/40" data-id="element-691" data-mp="1y3sl2vj7zoel">Address</span>
                  <textarea value={location.address} onChange={event => onUpdate(index, {
                address: event.target.value
              })} rows={2} placeholder="e.g. Jebel Jais, Wadi Shehah, Ras Al Khaimah" className={`${FIELD} resize-none`} data-id="element-692" data-mp="1u38njquqiirx" />
                </label>

                <label className="block sm:col-span-3" data-id="element-693" data-mp="1f6qnmpdui6nj">
                  <span className="label block text-paper/40" data-id="element-694" data-mp="1f55th0vg0pq7">Google Maps Link</span>
                  <input value={location.mapUrl} onChange={event => onUpdate(index, {
                mapUrl: event.target.value
              })} placeholder="Paste a Google Maps link for this place" className={FIELD} data-id="element-695" data-mp="108g7koelshvm" />
                </label>
              </div>

              <button type="button" onClick={() => onRemove(index)} aria-label={`Remove ${location.name.trim() || 'this location'}`} className="shrink-0 text-paper/30 transition-colors duration-150 ease-out hover:text-paper" data-id="element-696" data-mp="2lnci0vsb2rx9">
                <XIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-697" data-mp="ll33qvyc838i" />
              </button>
            </div>
          </li>)}
      </ul>

      <button type="button" onClick={onAdd} className={`${BUTTON_QUIET} mt-6 flex items-center gap-2`} data-id="element-698" data-mp="gkyqrfwn1qn1">
        <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-699" data-mp="cjmuirj9e75v" />
        Add Location
      </button>
    </section>;
}